import { msalInstance } from '../../msalConfig';
import { getToken, getSharePointBaseUrl, resolveOneDriveBaseUrl } from '../../sharePointConfig';

const combinePaths = (...paths) => {
    return paths
        .map(path => path.replace(/^[\\|/]/, "").replace(/[\\|/]$/, ""))
        .join("/")
        .replace(/\\/g, "/");
};

const pickerParams = {
    sdk: "8.0",
    entry: {
        oneDrive: {
            files: {},
        }
    },
    authentication: {},
    messaging: {
        origin: window.location.origin,
        channelId: "27"
    },
    selection: {
        mode: "multiple",
        enablePersistence: true
    },
    commands: {
        pick: {
            action: "select",
            label: "Selecionar",
            select: {
                urls: {
                    download: true
                }
            }
        }
    },
    typesAndSources: {
        filters: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.pdf', '.docx', '.xlsx'],
    }
};

const folderPickerParams = {
    sdk: "8.0",
    entry: {
        oneDrive: {
            files: {},
        }
    },
    authentication: {},
    messaging: {
        origin: window.location.origin,
        channelId: "28"
    },
    selection: {
        mode: "single",
        enablePersistence: false
    },
    commands: {
        pick: {
            action: "select",
            label: "Selecionar"
        }
    },
    typesAndSources: {
        mode: "folders",
    }
};

/**
 * Opens the OneDrive/SharePoint file picker.
 * @param {string} mode - "files" (default) for file selection, "folder" for single folder selection.
 * @param {object} options - Extra options to customize the picker. (ex: { filters: ['.pdf', '.docx'] }).
 * @returns {Promise<object|object[]|null>} - For "files": raw picker result (items/data). For "folder": { id, name } or null.
 */
export const openOneDrivePicker = async (mode = "files", options = {}) => {
    let params = mode === "folder" ? folderPickerParams : JSON.parse(JSON.stringify(pickerParams));

    if (mode === "files" && options.filters && Array.isArray(options.filters)) {
        params.typesAndSources.filters = options.filters;
    }

    const win = window.open(
        "",
        mode === "folder" ? "PickerFolder" : "Picker",
        "width=800,height=600"
    );
 
    if (!win) {
        throw new Error('Popup blocked');
    }

    win.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>Conectando ao OneDrive...</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100vh;
                background: #f3f3f3;
                color: #555;
                gap: 16px;
              }
              .spinner {
                width: 36px;
                height: 36px;
                border: 3px solid #ddd;
                border-top-color: #0078D4;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
              }
              @keyframes spin { to { transform: rotate(360deg); } }
              p { font-size: 15px; }
            </style>
          </head>
          <body>
            <div class="spinner"></div>
            <p>Conectando ao OneDrive...</p>
          </body>
        </html>
    `);
    win.document.close();

    let account = msalInstance.getActiveAccount();
    if (!account) {
        await msalInstance.loginPopup({ scopes: ["User.Read", "Files.Read"] });
    }

    let baseUrl;
    try {
        baseUrl = await resolveOneDriveBaseUrl();
    } catch (e) {
        alert("Não foi possível localizar o OneDrive deste usuário.");
        return null;
    }

    const authToken = await getToken({
        resource: baseUrl,
        type: "SharePoint",
    });


    const queryString = new URLSearchParams({
        filePicker: JSON.stringify(params),
    });

    const url = combinePaths(baseUrl, `_layouts/15/FilePicker.aspx?${queryString}`);

    const form = win.document.createElement("form");
    form.setAttribute("action", url);
    form.setAttribute("method", "POST");
    win.document.body.append(form);

    const input = win.document.createElement("input");
    input.setAttribute("type", "hidden");
    input.setAttribute("name", "access_token");
    input.setAttribute("value", authToken);
    form.appendChild(input);

    form.submit();

    return await new Promise((resolve) => {
        let pickerPort = null;

        const messageListener = async (message) => {
            if (message.data.type !== "command") {
                return;
            }

            if (pickerPort) {
                pickerPort.postMessage({
                    type: "acknowledge",
                    id: message.data.id,
                });
            }

            const command = message.data.data;

            if (command.command === "authenticate") {
                const token = await getToken({
                    resource: command.resource,
                    type: command.type || "SharePoint",
                });

                if (pickerPort) {
                    pickerPort.postMessage({
                        type: "result",
                        id: message.data.id,
                        data: {
                            result: "token",
                            token,
                        }
                    });
                }
                return;
            }

            if (command.command === "pick") {
                const raw = command.items || command.data;
                if (mode === "folder") {
                    const items = Array.isArray(raw) ? raw : (raw?.items || raw?.data || []);
                    const folder = items?.length > 0 ? items[0] : null;
                    const driverId = folder.parentReference?.driveId ?? folder.id;
                    console.log(folder)
                    console.log(items)
                    resolve(folder && driverId && folder.id && folder.name ? { drive_id: driverId, folder_id: folder.id, folder_name: folder.name} : null);
                } else {
                    resolve(raw);
                }
                win.close();
                return;
            }

            if (command.command === "close") {
                win.close();
                resolve(null);
            }
        };

        window.addEventListener("message", (event) => {
            if (event.source !== win) {
                return;
            }

            if (event.data.type === "initialize" && event.data.channelId === params.messaging.channelId) {
                pickerPort = event.ports[0];
                pickerPort.addEventListener("message", messageListener);
                pickerPort.start();
                pickerPort.postMessage({ type: "activate" });
            }
        });
    });
};